import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useScrollToTop, useScrollToTopOnChange } from '../utils/scrollUtils';
import {
  X,
  Star,
  MapPin,
  Clock,
  Navigation,
  Heart,
  Share2,
  Car,
  ExternalLink,
  Camera,
  Utensils,
  UtensilsCrossed,
  Fish,
  Apple,
  IceCream,
  Coffee,
  Flame,
  Waves,
  Shield,
  Sun,
  Droplet,
  TreePine,
  Accessibility,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import AreaLineChart from './AreaLineChart';
import { getLocalCurrentDateTime, useDateHandler, getDayOfWeek } from '../utils/dates';
import DayOfWeekSelector from './DayOfWeekSelector';
import { eventLogger, getData } from '../utils/auth';
import beachesParkingData from '../assets/beaches_parking.json';
import { BusynessTab, ParkingTab, TrafficTab, CrowdLevelChart, calculateDistance } from './LocationModal';
import './Modal.css';

interface BeachModalProps {
  details: {
    _id?: string;
    name: string;
    details: any;
    beachData?: any;
  } | null;
  handleClose: () => void;
}

// Generate synthetic capacity data for any beach
// This function creates realistic beach capacity patterns that can be used for any beach location
const generateBeachCapacities = (beachName?: string): Record<string, number> => {
  const capacities: Record<string, number> = {};
  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  days.forEach((day) => {
    // Generate hourly data from 6am to 10pm
    for (let hour = 6; hour <= 22; hour++) {
      const timeStr = `${hour.toString().padStart(2, '0')}:00:00`;
      const datetime = `${day} ${timeStr}`;

      // Beach capacity patterns (generic for all beaches):
      // - Weekends are busier (60-95%)
      // - Weekdays are quieter (30-70%)
      // - Peak times: 10am-4pm (especially weekends)
      // - Early morning (6-8am): 20-40%
      // - Evening (6-10pm): 40-60%

      const isWeekend = day === 'Saturday' || day === 'Sunday';
      const isPeakHour = hour >= 10 && hour <= 16;
      const isEarlyMorning = hour >= 6 && hour < 9;
      const isEvening = hour >= 18;

      let capacity = 0;

      if (isWeekend) {
        if (isPeakHour) {
          capacity = 75 + Math.floor(Math.random() * 20); // 75-95%
        } else if (isEarlyMorning) {
          capacity = 25 + Math.floor(Math.random() * 15); // 25-40%
        } else if (isEvening) {
          capacity = 45 + Math.floor(Math.random() * 15); // 45-60%
        } else {
          capacity = 60 + Math.floor(Math.random() * 15); // 60-75%
        }
      } else {
        // Weekday
        if (isPeakHour) {
          capacity = 50 + Math.floor(Math.random() * 20); // 50-70%
        } else if (isEarlyMorning) {
          capacity = 20 + Math.floor(Math.random() * 10); // 20-30%
        } else if (isEvening) {
          capacity = 35 + Math.floor(Math.random() * 15); // 35-50%
        } else {
          capacity = 40 + Math.floor(Math.random() * 15); // 40-55%
        }
      }

      capacities[datetime] = Math.min(100, Math.max(0, capacity));
    }
  });

  return capacities;
};

// Beach Overview Tab Component
const BeachOverviewTab = ({
  getCurrentCapacity,
  getBusynessColor,
  parkTime,
  bestTimeToVisit,
  locationDetails,
  convertedCurrentDateTime,
  getDayCapacities,
  beachData,
}) => {
  const currentCapacity = getCurrentCapacity();

  return (
    <motion.div
      key="overview"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="tab-content"
    >
      <div className="overview-section">
        {/* Crowd Level Chart */}
        <CrowdLevelChart
          convertedCurrentDateTime={convertedCurrentDateTime}
          getDayCapacities={getDayCapacities}
          style={{ marginTop: 0 }}
        />

        {/* Beach-Specific Information Categories */}

        {/* Safety Category */}
        {(beachData?.lifeguards_on_the_beach?.present ||
          beachData?.red_and_yellow_flags?.present ||
          beachData?.best_place_to_buy_sunscreen ||
          beachData?.wheelchair_access ||
          beachData?.general_safety_notes) && (
          <div className="food-category">
            <h3 className="food-category-title">
              <Shield size={20} />
              Safety
            </h3>
            <div className="food-options-grid">
              {/* Sunscreen */}
              {beachData?.best_place_to_buy_sunscreen && (
                <div className="food-option-card">
                  <div className="food-card-header">
                    <h4>Sunscreen</h4>
                  </div>
                  {beachData.best_place_to_buy_sunscreen.locations?.length > 0 && (
                    <p className="food-price">{beachData.best_place_to_buy_sunscreen.locations[0]}</p>
                  )}
                  {beachData.best_place_to_buy_sunscreen.avg_cost && (
                    <p className="food-price">{beachData.best_place_to_buy_sunscreen.avg_cost}</p>
                  )}
                </div>
              )}

              {/* Swimming Flags */}
              {beachData?.red_and_yellow_flags?.present && (
                <div className="food-option-card">
                  <div className="food-card-header">
                    <h4>Swimming Flags</h4>
                  </div>
                  <p className="food-price">Red and yellow flags present</p>
                </div>
              )}

              {/* Lifeguards */}
              {beachData?.lifeguards_on_the_beach?.present && (
                <div className="food-option-card">
                  <div className="food-card-header">
                    <h4>Lifeguards</h4>
                  </div>
                  <p className="food-price">Lifeguards on duty</p>
                  {beachData.lifeguards_on_the_beach.uniform && (
                    <p className="food-price food-price-detail">{beachData.lifeguards_on_the_beach.uniform}</p>
                  )}
                  {beachData.lifeguards_on_the_beach.notes && (
                    <p className="food-price food-price-detail">{beachData.lifeguards_on_the_beach.notes}</p>
                  )}
                </div>
              )}

              {/* Accessibility */}
              {beachData?.wheelchair_access && (
                <div className="food-option-card">
                  <div className="food-card-header">
                    <h4>Accessibility</h4>
                  </div>
                  {Object.entries(beachData.wheelchair_access).map(
                    ([key, value]: [string, any]) =>
                      value && (
                        <p key={key} className="food-price">
                          {key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                        </p>
                      )
                  )}
                </div>
              )}

              {/* General Safety Notes */}
              {beachData?.general_safety_notes && beachData.general_safety_notes.length > 0 && (
                <div className="food-option-card safety-notes-card-full-width">
                  <div className="food-card-header">
                    <h4>Safety Notes</h4>
                  </div>
                  <ul className="beach-amenities-list">
                    {beachData.general_safety_notes.map((note: string, index: number) => (
                      <li key={`safety-note-${index}`}>{note}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Amenities Category */}
        {(beachData?.nearby_toilets?.length > 0 ||
          beachData?.nearby_showers?.length > 0 ||
          beachData?.shade_areas?.length > 0) && (
          <div className="food-category">
            <h3 className="food-category-title">
              <Droplet size={20} />
              Amenities
            </h3>
            <div className="food-options-grid">
              {/* Toilets - Grouped */}
              {beachData?.nearby_toilets?.length > 0 && (
                <div className="food-option-card">
                  <div className="food-card-header">
                    <h4>Toilets</h4>
                  </div>
                  <ul className="beach-amenities-list">
                    {beachData.nearby_toilets.map((toilet: string, index: number) => (
                      <li key={`toilet-${index}`}>{toilet}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Showers - Grouped */}
              {beachData?.nearby_showers?.length > 0 && (
                <div className="food-option-card">
                  <div className="food-card-header">
                    <h4>Showers</h4>
                  </div>
                  <ul className="beach-amenities-list">
                    {beachData.nearby_showers.map((shower: string, index: number) => (
                      <li key={`shower-${index}`}>{shower}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Shade Areas - Grouped */}
              {beachData?.shade_areas?.length > 0 && (
                <div className="food-option-card">
                  <div className="food-card-header">
                    <h4>Shade Areas</h4>
                  </div>
                  <ul className="beach-amenities-list">
                    {beachData.shade_areas.map((area: string, index: number) => (
                      <li key={`shade-${index}`}>{area}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Rockpools and Pools Category */}
        {beachData?.nearby_rockpools && Object.keys(beachData.nearby_rockpools).length > 0 && (
          <div className="food-category">
            <h3 className="food-category-title">
              <Waves size={20} />
              Rockpools & Pools
            </h3>
            <div className="food-options-grid">
              {Object.entries(beachData.nearby_rockpools).map(([key, value]: [string, any]) => (
                <div key={key} className="food-option-card">
                  <div className="food-card-header">
                    <h4>{key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}</h4>
                  </div>
                  <p className="food-price">{value}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// Food Tab Component
const FoodTab = ({ beachData }) => {
  if (!beachData?.food) {
    return (
      <motion.div
        key="food"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        className="tab-content"
      >
        <div className="food-section">
          <p>No food information available</p>
        </div>
      </motion.div>
    );
  }

  const { food } = beachData;

  return (
    <motion.div
      key="food"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="tab-content"
    >
      <div className="food-section">
        {/* Burgers & Sandwiches */}
        {food.best_place_to_buy_burger_sandwich?.options && (
          <div className="food-category">
            <h3 className="food-category-title">
              <UtensilsCrossed size={18} />
              Burgers & Sandwiches
            </h3>
            <div className="food-options-grid">
              {food.best_place_to_buy_burger_sandwich.options.map((option: any, index: number) => (
                <div key={index} className="food-option-card">
                  <h4>{option.name}</h4>
                  {option.avg_cost_standard_sandwich && (
                    <p className="food-price">{option.avg_cost_standard_sandwich}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Seafood */}
        {food.best_places_to_buy_seafood?.options && (
          <div className="food-category">
            <h3 className="food-category-title">
              <Fish size={18} />
              Seafood
            </h3>
            <div className="food-options-grid">
              {food.best_places_to_buy_seafood.options.map((option: any, index: number) => (
                <div key={index} className="food-option-card">
                  <h4>{option.name}</h4>
                  {option.avg_cost_fish_and_chips && <p className="food-price">{option.avg_cost_fish_and_chips}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Acai */}
        {food.best_places_to_buy_acai?.options && (
          <div className="food-category">
            <h3 className="food-category-title">
              <Apple size={18} />
              Acai Bowls
            </h3>
            <div className="food-options-grid">
              {food.best_places_to_buy_acai.options.map((option: any, index: number) => (
                <div key={index} className="food-option-card">
                  <h4>{option.name}</h4>
                  {option.avg_cost_standard_bowl && <p className="food-price">{option.avg_cost_standard_bowl}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Ice Cream */}
        {food.best_place_to_buy_ice_cream?.options && (
          <div className="food-category">
            <h3 className="food-category-title">
              <IceCream size={18} />
              Ice Cream
            </h3>
            <div className="food-options-grid">
              {food.best_place_to_buy_ice_cream.options.map((option: any, index: number) => (
                <div key={index} className="food-option-card">
                  <h4>{option.name}</h4>
                  {option.avg_cost_single_scoop && <p className="food-price">{option.avg_cost_single_scoop}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Coffee */}
        {food.best_places_to_buy_coffee?.options && (
          <div className="food-category">
            <h3 className="food-category-title">
              <Coffee size={18} />
              Coffee
            </h3>
            <div className="food-options-grid">
              {food.best_places_to_buy_coffee.options.map((option: any, index: number) => (
                <div key={index} className="food-option-card">
                  <h4>{option.name}</h4>
                  {option.prices && (
                    <div className="food-prices">
                      {option.prices.espresso_piccolo_macchiato && (
                        <span>Espresso/Piccolo: {option.prices.espresso_piccolo_macchiato}</span>
                      )}
                      {option.prices.espresso_piccolo && (
                        <span>Espresso/Piccolo: {option.prices.espresso_piccolo}</span>
                      )}
                      {option.prices.regular_coffee && <span>Regular: {option.prices.regular_coffee}</span>}
                      {option.prices.large_coffee && <span>Large: {option.prices.large_coffee}</span>}
                      {option.prices.batch_brew && <span>Batch Brew: {option.prices.batch_brew}</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Public BBQ Spots */}
        {food.nearby_public_bbq_spots && food.nearby_public_bbq_spots.length > 0 && (
          <div className="food-category">
            <h3 className="food-category-title">
              <Flame size={18} />
              Public BBQ Spots
            </h3>
            <div className="food-options-grid">
              {food.nearby_public_bbq_spots.map((spot: string, index: number) => (
                <div key={index} className="food-option-card">
                  <h4>{spot}</h4>
                  <p className="food-price">Free</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

const BeachModal: React.FC<BeachModalProps> = ({ details, handleClose }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'busyness' | 'parking' | 'traffic' | 'food'>('overview');

  const headerRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Measure header and tabs height for content positioning
  useEffect(() => {
    const updateHeights = () => {
      if (headerRef.current && tabsRef.current && modalRef.current) {
        const headerHeight = headerRef.current.offsetHeight;
        const tabsHeight = tabsRef.current.offsetHeight;
        modalRef.current.style.setProperty('--modal-header-height', `${headerHeight}px`);
        modalRef.current.style.setProperty('--modal-tabs-height', `${tabsHeight}px`);
      }
    };

    updateHeights();
    window.addEventListener('resize', updateHeights);
    return () => window.removeEventListener('resize', updateHeights);
  }, [details?.name]);

  // Use scroll-to-top utility hook with automatic scrolling on tab changes
  const { scrollToTop } = useScrollToTopOnChange([activeTab]);
  const [selectedDay, setSelectedDay] = useState<string>('');
  const [parkingData, setParkingData] = useState<any>(null);
  const {
    convertedCurrentDateTime,
    setConvertedCurrentDateTime,
    selectedDateTimes,
    setSelectedDateTimes,
    setUpDate,
    setUpAllDates,
    getDayCapacities,
    getCurrentCapacity,
  } = useDateHandler();

  useEffect(() => {
    // Validate coordinates before calling getLocalCurrentDateTime
    const lat = details?.details?.latitude;
    const lng = details?.details?.longitude;
    const isValidCoordinate = (coord: number | undefined | null): boolean => {
      if (coord === undefined || coord === null) return false;
      if (typeof coord !== 'number') return false;
      if (isNaN(coord) || !isFinite(coord)) return false;
      return true;
    };

    const hasValidCoords =
      isValidCoordinate(lat) &&
      isValidCoordinate(lng) &&
      typeof lat === 'number' &&
      typeof lng === 'number' &&
      lat >= -90 &&
      lat <= 90 &&
      lng >= -180 &&
      lng <= 180;

    const currentDateTime = hasValidCoords ? getLocalCurrentDateTime(lat!, lng!) : getLocalCurrentDateTime();
    setConvertedCurrentDateTime(setUpDate(currentDateTime.dateTime));

    // Use synthetic capacities if not provided
    const capacities = details?.details?.capacities || generateBeachCapacities(details?.details?.location);
    setUpAllDates(capacities);
    setSelectedDay(currentDateTime.day);
  }, [details]);

  useEffect(() => {
    setSelectedDateTimes(getDayCapacities(selectedDay));
  }, [selectedDay]);

  // Load parking data from beaches_parking.json or API fallback
  useEffect(() => {
    eventLogger('location_tab', { location: details?.name, tab: activeTab });
    if (activeTab === 'parking' && !parkingData) {
      const beachName = details?.name || details?.details?.location;

      // First try to load from beaches_parking.json
      if (beachName && beachesParkingData[beachName as keyof typeof beachesParkingData]) {
        console.log('🚗 Loading parking data from beaches_parking.json for:', beachName);
        setParkingData(beachesParkingData[beachName as keyof typeof beachesParkingData]);
      } else {
        // Fallback to API
        console.log('🚗 Fetching parking data from API for beach:', beachName);
        getData(`/centreparkinginfo/${beachName}`)
          .then((data) => setParkingData(data))
          .catch(() => console.log('No parking data found'));
      }
    }
  }, [activeTab, details]);

  if (!details) return null;

  const { name, details: locationDetails } = details;
  // Get beachData from either details.beachData or details.details.beachData
  const beachData = details.beachData || locationDetails?.beachData;
  const capacities = locationDetails?.capacities || generateBeachCapacities(locationDetails?.location);

  console.log('🏖️ BeachModal - details:', details);
  console.log('🏖️ BeachModal - beachData:', beachData);
  console.log('🏖️ BeachModal - locationDetails:', locationDetails);

  const getParkTime = (capacity: number) => {
    if (capacity < 40) return '< 1 min';
    if (capacity < 70) return '1 - 3 mins';
    if (capacity < 85) return '3 - 5 mins';
    if (capacity < 95) return '5 - 10 mins';
    return '> 10 mins';
  };

  const getBestTimeToVisit = (capacities: any): string => {
    if (!capacities || Object.keys(capacities).length === 0) return 'Check crowds tab';

    const dayCapacities = getDayCapacities(convertedCurrentDateTime);
    if (dayCapacities.length === 0) return 'Check crowds tab';

    const lowestCapacity = dayCapacities.reduce((min, [_, capacity]) => Math.min(min, Number(capacity)), 100);

    if (lowestCapacity < 30) return 'Very quiet - great time!';
    if (lowestCapacity < 50) return 'Low crowds - good time';
    if (lowestCapacity < 70) return 'Moderate crowds';
    return 'Usually busy - check crowds tab';
  };

  const parkTime = getParkTime(getCurrentCapacity());
  const bestTimeToVisit = getBestTimeToVisit(capacities);

  const handleShare = () => {
    if (navigator.share) {
      navigator
        .share({
          title: `Check out ${name} on VibeCheck!`,
          text: `I found this great beach with ${getCurrentCapacity()}% crowd level!`,
          url: window.location.href,
        })
        .catch((error) => {
          if (error.name !== 'AbortError') {
            console.log('Share error:', error);
          }
        });
    }
  };

  const getBusynessColor = (busyness: number): string => {
    if (busyness < 30) return '#4CAF50';
    if (busyness < 60) return '#FF9800';
    if (busyness < 80) return '#FF5722';
    return '#F44336';
  };

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="modal-overlay fullscreen"
      onClick={handleClose}
    >
      <motion.div
        ref={modalRef}
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="location-modal fullscreen"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div ref={headerRef} className="modal-header fullscreen">
          <div className="header-left">
            <h1 className="location-title">{name}</h1>
          </div>
          <div className="header-right">
            <button onClick={handleShare} className="icon-action-button" title="Share">
              <Share2 size={18} />
            </button>
            <button onClick={handleClose} className="icon-action-button close" title="Close">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div ref={tabsRef} className="modal-tabs">
          {[
            { id: 'overview', label: 'Overview', icon: MapPin },
            { id: 'busyness', label: 'Crowds', icon: Clock },
            { id: 'food', label: 'Food', icon: Utensils },
            { id: 'parking', label: 'Parking', icon: Car },
            { id: 'traffic', label: 'Traffic', icon: Camera },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              data-tab={id}
              onClick={() => {
                setActiveTab(id as any);
              }}
              className={`tab-button ${activeTab === id ? 'active' : ''}`}
            >
              <Icon size={18} />
              <span>{label}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <motion.div className="modal-content fullscreen">
          <AnimatePresence mode="wait">
            {activeTab === 'overview' && (
              <BeachOverviewTab
                getCurrentCapacity={getCurrentCapacity}
                getBusynessColor={getBusynessColor}
                parkTime={parkTime}
                bestTimeToVisit={bestTimeToVisit}
                locationDetails={locationDetails}
                convertedCurrentDateTime={convertedCurrentDateTime}
                getDayCapacities={getDayCapacities}
                beachData={beachData}
              />
            )}

            {activeTab === 'busyness' && (
              <BusynessTab
                convertedCurrentDateTime={convertedCurrentDateTime}
                getCurrentCapacity={getCurrentCapacity}
                getParkTime={getParkTime}
                getDayCapacities={getDayCapacities}
                capacities={capacities}
                selectedDay={selectedDay}
                setSelectedDay={setSelectedDay}
                selectedDateTimes={selectedDateTimes}
                name={details?.name}
              />
            )}

            {activeTab === 'food' && <FoodTab beachData={beachData} />}

            {activeTab === 'parking' && <ParkingTab details={details} parkingData={parkingData} />}

            {activeTab === 'traffic' && <TrafficTab details={details} />}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </motion.div>,
    document.body
  );
};

export default BeachModal;
